import { SCREEN_H, SCREEN_W } from "../engine/gfx.ts";
import { drawText, measureText } from "../engine/font.ts";
import { actorFrame, type Outfit } from "../world/actors.ts";
import { bayer, hash2 } from "../world/noise.ts";
import { itemIcon } from "./icons.ts";
import { hex, mix, shade, W, type Col } from "./pal.ts";

export const NURSE: Outfit = { skin: hex("#f6c9a0"), hair: hex("#ff9ab4"), hairStyle: "bun", hat: { color: hex("#ffffff"), kind: "beanie" }, shirt: hex("#ff8fb0"), trim: hex("#ffffff"), pants: hex("#f4f1ea"), shoes: hex("#ff6f91") };
export const VENDOR: Outfit = { skin: hex("#d9a882"), hair: hex("#3a2a2a"), hairStyle: "short", hat: { color: hex("#40c4b0"), kind: "cap" }, shirt: hex("#40c4b0"), trim: hex("#fff6e6"), pants: hex("#34426e"), shoes: hex("#2a2340") };

const cache = new Map<string, ImageData>();

function paint(img: ImageData, x: number, y: number, c: Col): void {
  if (x < 0 || y < 0 || x >= img.width || y >= img.height) return;
  const o = (y * img.width + x) * 4;
  const a = c[3] / 255;
  img.data[o] = img.data[o]! + (c[0] - img.data[o]!) * a;
  img.data[o + 1] = img.data[o + 1]! + (c[1] - img.data[o + 1]!) * a;
  img.data[o + 2] = img.data[o + 2]! + (c[2] - img.data[o + 2]!) * a;
  img.data[o + 3] = 255;
}

function rect(img: ImageData, x: number, y: number, w: number, h: number, c: Col | ((x: number, y: number) => Col)): void {
  for (let yy = y; yy < y + h; yy++) for (let xx = x; xx < x + w; xx++) paint(img, xx, yy, typeof c === "function" ? c(xx, yy) : c);
}

function stamp(img: ImageData, src: ImageData, dx: number, dy: number, scale = 1): void {
  for (let y = 0; y < src.height * scale; y++) {
    for (let x = 0; x < src.width * scale; x++) {
      const o = ((Math.floor(y / scale)) * src.width + Math.floor(x / scale)) * 4;
      if (src.data[o + 3]! < 128) continue;
      paint(img, dx + x, dy + y, [src.data[o]!, src.data[o + 1]!, src.data[o + 2]!, 255]);
    }
  }
}

function glowAt(img: ImageData, cx: number, cy: number, r: number, c: Col, k: number): void {
  for (let y = Math.floor(cy - r); y <= cy + r; y++) {
    for (let x = Math.floor(cx - r); x <= cx + r; x++) {
      if (x < 0 || y < 0 || x >= img.width || y >= img.height) continue;
      const d2 = ((x - cx) ** 2 + (y - cy) ** 2) / (r * r);
      if (d2 >= 1) continue;
      const f = (1 - d2) ** 2 * k;
      const o = (y * img.width + x) * 4;
      img.data[o] = img.data[o]! + c[0] * f;
      img.data[o + 1] = img.data[o + 1]! + c[1] * f;
      img.data[o + 2] = img.data[o + 2]! + c[2] * f;
    }
  }
}

/** Cozy interior backdrop for the clinic / stall screens. */
export function interior(kind: "clinic" | "shop", night: boolean): ImageData {
  const key = `${kind}:${night}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const img = new ImageData(SCREEN_W, SCREEN_H);
  const clinic = kind === "clinic";
  // Wallpaper.
  const wallA = clinic ? hex("#d8f2ea") : hex("#f4e2c4");
  const wallB = clinic ? hex("#c4e6dc") : hex("#e8d2ae");
  rect(img, 0, 0, SCREEN_W, 150, (x, y) => {
    const stripe = Math.floor(x / 12) % 2 === 0;
    let c = stripe ? wallA : wallB;
    if (!clinic && (x + y * 2) % 23 === 0) c = shade(c, 0.94);
    if (clinic && (x % 24 === 6 && y % 24 === 6)) c = hex("#ff9ab4");
    return mix(c, hex("#b0a0c8"), (y / 150) * 0.18);
  });
  // Crown moulding + wainscot.
  rect(img, 0, 0, SCREEN_W, 6, clinic ? hex("#8fd6c4") : W.wood[2]!);
  rect(img, 0, 6, SCREEN_W, 2, clinic ? hex("#5fae9e") : W.wood[3]!);
  rect(img, 0, 104, SCREEN_W, 46, (x, y) => {
    const panel = x % 48;
    let c = clinic ? hex("#fff6ea") : W.wood[1]!;
    if (panel < 2 || y === 104 || y === 105) c = clinic ? hex("#e2d6c6") : W.wood[3]!;
    if (y > 146) c = clinic ? hex("#cfc2b0") : W.wood[4]!;
    return c;
  });
  // Floor.
  rect(img, 0, 150, SCREEN_W, SCREEN_H - 150, (x, y) => {
    const row = Math.floor((y - 150) / 6);
    const plank = Math.floor((x + (row % 2) * 17) / 34);
    let c = clinic ? (((x >> 4) + (y >> 3)) % 2 ? hex("#f2eadc") : hex("#e0d6c6")) : (hash2(plank, row, 3) < 0.5 ? W.wood[1]! : W.wood[2]!);
    if (!clinic && ((y - 150) % 6 === 0 || (x + (row % 2) * 17) % 34 === 0)) c = W.wood[3]!;
    if (bayer(x, y) < 0.08) c = shade(c, 0.95);
    return mix(c, hex("#3a2a48"), ((y - 150) / 66) * 0.25);
  });
  // Window.
  const wx = clinic ? 150 : 36, wy = 22, ww = 96, wh = 64;
  rect(img, wx - 4, wy - 4, ww + 8, wh + 8, clinic ? hex("#ffffff") : W.wood[3]!);
  rect(img, wx, wy, ww, wh, (_x, y) => {
    const t = (y - wy) / wh;
    return night ? mix(hex("#1c2250"), hex("#3a2a72"), t) : mix(hex("#8fd0ff"), hex("#dff4ff"), t);
  });
  if (night) for (let i = 0; i < 14; i++) paint(img, wx + Math.floor(hash2(i, 1, 4) * ww), wy + Math.floor(hash2(i, 2, 4) * wh * 0.7), hex("#fff6d0"));
  else for (let i = 0; i < 3; i++) rect(img, wx + 10 + i * 28, wy + 12 + (i % 2) * 8, 18, 5, hex("#ffffff", 200));
  rect(img, wx + ww / 2 - 1, wy, 3, wh, clinic ? hex("#ffffff") : W.wood[3]!);
  rect(img, wx, wy + wh / 2 - 1, ww, 3, clinic ? hex("#ffffff") : W.wood[3]!);
  for (let k = 0; k < 40; k++) paint(img, wx + 4 + k, wy + 4 + k, hex("#ffffff", 70));
  // Counter.
  const cy = 112;
  rect(img, 0, cy, SCREEN_W, 40, (x, y) => {
    let c = clinic ? hex("#ffffff") : W.wood[1]!;
    if (y < cy + 5) c = clinic ? hex("#9be3d0") : W.wood[0]!;
    if (y === cy + 5) c = clinic ? hex("#5fae9e") : W.wood[3]!;
    if (y > cy + 5 && x % 64 < 2) c = clinic ? hex("#e2ecea") : W.wood[2]!;
    return c;
  });
  // Staff behind the counter.
  const staff = actorFrame(clinic ? "npc:nurse" : "npc:vendor", clinic ? NURSE : VENDOR, 1, 0);
  stamp(img, staff, clinic ? 276 : 292, cy - 70, 3);
  rect(img, 0, cy, SCREEN_W, 6, clinic ? hex("#9be3d0") : W.wood[0]!);
  rect(img, 0, cy + 6, SCREEN_W, 1, clinic ? hex("#5fae9e") : W.wood[3]!);
  if (clinic) {
    // Kettle station + cross sign.
    rect(img, 334, cy - 16, 26, 16, hex("#9fc4e8"));
    rect(img, 336, cy - 20, 22, 6, hex("#e4f4ff"));
    rect(img, 358, cy - 14, 6, 3, hex("#5d7fb0"));
    for (let i = 0; i < 5; i++) glowAt(img, 344 + i * 2, cy - 28 - i * 6, 5, [255, 255, 255, 255], 0.35);
    rect(img, 336, 20, 30, 30, hex("#ff6f91"));
    rect(img, 347, 25, 8, 20, hex("#ffffff"));
    rect(img, 341, 31, 20, 8, hex("#ffffff"));
    // Plant.
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI;
      for (let r = 0; r < 14; r++) paint(img, Math.round(262 + Math.cos(a) * r), Math.round(cy - 10 - Math.sin(a) * r * 1.3), r > 10 ? W.leaf[1]! : W.leaf[3]!);
    }
    rect(img, 254, cy - 10, 16, 10, hex("#e8a07a"));
  } else {
    // Shelves of stock.
    for (let s = 0; s < 3; s++) {
      const sy = 26 + s * 26;
      rect(img, 150, sy + 18, 220, 4, W.wood[3]!);
      rect(img, 150, sy + 18, 220, 1, W.wood[1]!);
      const stock: ("capsule" | "gloss" | "myth" | "sip" | "fullcope" | "cleartea")[] = ["capsule", "sip", "gloss", "fullcope", "cleartea", "myth"];
      for (let k = 0; k < 11; k++) {
        const id = stock[(k + s * 2) % stock.length]!;
        stamp(img, itemIcon(id), 152 + k * 20, sy);
      }
    }
    // Sign.
    const label = "SIGNAL STALL";
    const lw = measureText(label) + 16;
    rect(img, 36, 94, lw, 14, W.wood[3]!);
    rect(img, 37, 95, lw - 2, 12, hex("#ffc84a"));
    drawText(img, 44, 98, label, [43, 34, 56, 255]);
    // Hanging lamp.
    rect(img, 110, 0, 1, 16, hex("#2c3048"));
    rect(img, 104, 16, 13, 6, hex("#474e6d"));
    glowAt(img, 110, 26, 40, [255, 200, 120, 255], night ? 0.5 : 0.25);
  }
  if (night) {
    for (let i = 0; i < img.data.length; i += 4) {
      img.data[i] = img.data[i]! * 0.78; img.data[i + 1] = img.data[i + 1]! * 0.74; img.data[i + 2] = img.data[i + 2]! * 0.86;
    }
    glowAt(img, clinic ? 300 : 110, clinic ? 90 : 26, 90, [255, 190, 110, 255], 0.35);
  }
  cache.set(key, img);
  return img;
}
